<template>
    <section class="internal-page case-detail">
        <header class="case-hero">
            <div class="pbb-container">
                <nav class="case-hero__breadcrumb" aria-label="Caminho de navegação">
                    <router-link to="/">Início</router-link>
                    <span aria-hidden="true">/</span>
                    <router-link :to="backTo">Todos os processos</router-link>
                </nav>

                <page-state
                    v-if="loading || error"
                    :loading="loading"
                    :error="error"
                    @retry="load"
                />

                <div v-else class="case-hero__layout">
                    <div class="case-hero__content">
                        <span class="case-hero__nature">
                            <i aria-hidden="true"></i>
                            {{ nature }}
                        </span>
                        <h1>{{ dataset.data.name || 'Detalhes do processo' }}</h1>
                        <div class="case-hero__process">
                            <span>Número do processo</span>
                            <strong>{{ dataset.data.process_number || 'Não informado' }}</strong>
                        </div>
                    </div>

                    <aside class="case-hero__aside">
                        <div class="case-hero__logo">
                            <img
                                v-if="dataset.data.logo_url"
                                :src="dataset.data.logo_url"
                                :alt="dataset.data.name"
                            />
                            <span v-else>{{ initials(dataset.data.name) }}</span>
                        </div>
                        <div v-if="latestRequest" class="case-hero__request" role="status">
                            <span>Sua {{ requestTypeName(latestRequest) }}</span>
                            <strong>{{ requestStatusName(latestRequest) }}</strong>
                            <small
                                >Enviada em
                                {{ $moment(latestRequest.created_at).format('DD.MM.YYYY') }}</small
                            >
                            <router-link to="/habilitacoes-divergencias" class="pbb-focus"
                                >Ver minha solicitação</router-link
                            >
                        </div>
                        <a
                            v-if="externalSite"
                            :href="externalSite"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="case-hero__action pbb-focus"
                        >
                            Acessar site do caso
                            <v-icon size="19">mdi-open-in-new</v-icon>
                        </a>
                        <button
                            v-if="isBeforeEndEventDate && !hasPendingRequest"
                            type="button"
                            class="case-hero__action pbb-focus"
                            @click="navigateToCreateUserEvents(dataset.data.id)"
                        >
                            {{
                                latestRequest
                                    ? 'Enviar divergência'
                                    : 'Enviar habilitação ou divergência'
                            }}
                            <v-icon size="19">mdi-arrow-right</v-icon>
                        </button>
                        <router-link
                            v-if="isBankruptcy"
                            :to="{ path: '/solicitacao-ppp', query: { id: dataset.data.id } }"
                            class="case-hero__action pbb-focus"
                        >
                            Solicitar PPP
                            <v-icon size="19">mdi-arrow-right</v-icon>
                        </router-link>
                    </aside>
                </div>
            </div>
        </header>

        <main v-if="!loading && !error" class="pbb-container case-content">
            <section
                class="case-facts"
                :class="{ 'case-facts--five': isRecovery }"
                aria-label="Resumo do processo"
            >
                <article class="case-fact case-fact--wide">
                    <span>Comarca</span>
                    <strong>{{ districtName }}</strong>
                </article>
                <article class="case-fact">
                    <span>Data do pedido</span>
                    <strong>{{ formatDate(dataset.data.judgment_at) }}</strong>
                </article>
                <article class="case-fact">
                    <span>Deferimento</span>
                    <strong>{{ formatDate(dataset.data.deferment_at) }}</strong>
                </article>
                <article v-if="isRecovery" class="case-fact">
                    <span>Concessão</span>
                    <strong>{{ formatDate(dataset.data.concession_at) }}</strong>
                </article>
                <article class="case-fact">
                    <span>Documentos</span>
                    <strong>{{ formatCount(documentCount) }}</strong>
                </article>
            </section>

            <section class="case-overview">
                <article class="case-overview__main">
                    <span class="case-section-label">Visão geral</span>
                    <h2>Informações do processo</h2>
                    <div
                        v-if="descriptionHtml"
                        class="case-overview__description internal-rich-text"
                        v-html="descriptionHtml"
                    ></div>
                    <p v-else class="case-overview__fallback">
                        As informações públicas deste processo serão disponibilizadas nesta área.
                    </p>
                </article>

                <aside class="case-overview__aside">
                    <span class="case-section-label">Dados processuais</span>
                    <dl>
                        <div>
                            <dt>Vara responsável</dt>
                            <dd>{{ dataset.data.local || 'Não informada' }}</dd>
                        </div>
                        <div>
                            <dt>Administrador responsável</dt>
                            <dd>{{ employeeName }}</dd>
                        </div>
                        <div>
                            <dt>Natureza</dt>
                            <dd>{{ nature }}</dd>
                        </div>
                    </dl>
                </aside>
            </section>

            <section v-if="timeline.length" class="case-timeline">
                <header class="case-documents__header">
                    <div>
                        <span class="case-section-label">Linha do tempo</span>
                        <h2>Andamento do processo</h2>
                    </div>
                    <p>Principais atos e datas do processo, do pedido até o momento atual.</p>
                </header>
                <ol class="case-timeline__list">
                    <li v-for="event in timeline" :key="event.id" class="case-timeline__item">
                        <time :datetime="event.event_date">{{
                            formatTimelineDate(event.event_date)
                        }}</time>
                        <div>
                            <strong>{{ event.title }}</strong>
                            <p v-if="event.description">{{ event.description }}</p>
                        </div>
                    </li>
                </ol>
            </section>

            <section class="case-documents">
                <header class="case-documents__header">
                    <div>
                        <span class="case-section-label">Central documental</span>
                        <h2>Documentos do processo</h2>
                    </div>
                    <p>
                        Consulte atos, relatórios e atas organizados por categoria e ordem de
                        publicação.
                    </p>
                </header>

                <div class="case-documents__workspace">
                    <nav class="case-documents__tabs" aria-label="Categorias de documentos">
                        <button
                            v-for="(itemType, index) in dataset.clientDocTypes"
                            :key="itemType.id"
                            type="button"
                            :class="{ 'is-active': activeDocTab === index }"
                            :aria-current="activeDocTab === index ? 'page' : null"
                            class="pbb-focus"
                            @click="activeDocTab = index"
                        >
                            <span>{{ formatIndex(index + 1) }}</span>
                            <strong>{{ itemType.name }}</strong>
                            <small>{{ formatCount(itemType.documents.length) }}</small>
                        </button>
                    </nav>

                    <div class="case-documents__panel">
                        <div class="case-documents__panel-head">
                            <div>
                                <span>Categoria selecionada</span>
                                <h3>{{ activeDocumentType.name }}</h3>
                            </div>
                            <strong>{{ documentCountLabel(activeDocuments.length) }}</strong>
                        </div>

                        <div v-if="activeDocuments.length" class="document-list">
                            <a
                                v-for="(document, index) in activeDocuments"
                                :key="document.id || index"
                                :href="document.doc_url"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="document-row pbb-focus"
                            >
                                <span class="document-row__number">{{
                                    formatIndex(index + 1)
                                }}</span>
                                <span class="document-row__icon">
                                    <v-icon size="20">mdi-file-document-outline</v-icon>
                                </span>
                                <span class="document-row__content">
                                    <strong>{{ document.name }}</strong>
                                    <small>{{ documentDate(document) }}</small>
                                </span>
                                <span class="document-row__action">
                                    Abrir
                                    <v-icon size="19">mdi-arrow-top-right</v-icon>
                                </span>
                            </a>
                        </div>

                        <div v-else class="case-documents__empty">
                            <span class="case-documents__empty-icon">
                                <v-icon size="24">mdi-folder-open-outline</v-icon>
                            </span>
                            <div>
                                <strong>Nenhum documento publicado nesta categoria</strong>
                                <p>
                                    Novos documentos aparecerão aqui assim que forem
                                    disponibilizados.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </main>

        <v-dialog
            v-if="dataset.data.banner_url"
            v-model="dialog"
            max-width="600"
            hide-overlay
            @click:outside="closeDialog"
        >
            <v-card>
                <v-img :src="dataset.data.banner_url"></v-img>
                <v-card-actions>
                    <v-btn color="primary" @click="closeDialog">Fechar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </section>
</template>

<script>
import 'moment/locale/pt-br';
import axios from 'axios';
import { externalSite } from '@/common/util/case-link';

import { getClientDocTypes } from '@/common/client-doc-types.data';

export default {
    name: 'JudicialRecoveryEnterprise',
    props: {
        nature: { type: String, default: 'Recuperação judicial' },
        backTo: { type: String, default: '/recuperacao-judicial' }
    },
    data() {
        return {
            dataset: {
                data: {},
                clientDocTypes: []
            },
            loading: true,
            error: false,
            dialog: true,
            activeDocTab: 0,
            myRequests: []
        };
    },
    computed: {
        // caso com site próprio: a página de detalhes só aparece por link direto; mostra o atalho para o site
        externalSite() {
            return externalSite(this.dataset.data);
        },
        timeline() {
            return Array.isArray(this.dataset.data.timeline) ? this.dataset.data.timeline : [];
        },
        isBankruptcy() {
            return Number(this.dataset.data.type) === 2;
        },
        isRecovery() {
            return [1, 5].includes(Number(this.dataset.data.type));
        },
        latestRequest() {
            return this.myRequests[0] || null;
        },
        hasPendingRequest() {
            return this.myRequests.some((request) => request.status === 0);
        },
        isBeforeEndEventDate() {
            if (!this.dataset.data.end_event_at) return false;
            const endEventDate = new Date(this.dataset.data.end_event_at);
            return currentDateIsValid(endEventDate) && new Date() < endEventDate;
        },
        districtName() {
            return this.dataset.data.district ? this.dataset.data.district.name : 'Não informada';
        },
        employeeName() {
            return this.dataset.data.employee ? this.dataset.data.employee.name : 'Equipe PB&B';
        },
        documentCount() {
            return this.dataset.clientDocTypes.reduce(
                (total, item) => total + item.documents.length,
                0
            );
        },
        activeDocumentType() {
            return this.dataset.clientDocTypes[this.activeDocTab] || { name: '', documents: [] };
        },
        activeDocuments() {
            return this.activeDocumentType.documents || [];
        },
        descriptionHtml() {
            return (this.dataset.data.description || '')
                .replace(/<p>\s*Fonte pública:\s*https?:\/\/[^<]+<\/p>/gi, '')
                .trim();
        }
    },
    mounted() {
        this.load();
        this.loadMyRequests();
    },
    methods: {
        async load() {
            this.loading = true;
            this.error = false;
            try {
                const result = await axios.get(`/api/clients/${this.$route.params.id}`);
                this.dataset.data = result.data.content;

                const documents = Array.isArray(this.dataset.data.documents)
                    ? this.dataset.data.documents
                    : [];
                this.dataset.clientDocTypes = getClientDocTypes(
                    this.dataset.data.type,
                    documents
                ).map((item) => ({
                    ...item,
                    documents: documents.filter(
                        (document) => Number(document.type) === Number(item.id)
                    )
                }));
                this.activeDocTab = 0;
            } catch (error) {
                this.error = true;
            } finally {
                this.loading = false;
            }
        },
        async loadMyRequests() {
            if (!this.$root.user) return;
            try {
                const result = await axios.get('/api/user-events/request', {
                    skipAuthRedirect: true
                });
                const clientId = Number(this.$route.params.id);
                this.myRequests = (result.data.content || [])
                    .filter((request) => Number(request.client_id) === clientId)
                    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            } catch (error) {
                // A página do processo continua útil sem o resumo do credor.
                this.myRequests = [];
            }
        },
        requestTypeName(request) {
            return request.type == 2 ? 'divergência' : 'habilitação';
        },
        // A análise é feita fora da plataforma: o credor vê só "submetido" ou "análise concluída".
        requestStatusName(request) {
            return request.analysis_concluded
                ? 'Análise concluída'
                : 'Submetido para apreciação da AJ';
        },
        formatTimelineDate(date) {
            // A API devolve a data pura (AAAA-MM-DD); o moment a interpreta no fuso local.
            return this.$moment(date, 'YYYY-MM-DD').format('DD.MM.YYYY');
        },
        formatDate(date) {
            if (!date || date === '0000-00-00 00:00:00') return 'Não informado';
            const parsedDate = new Date(date);
            return currentDateIsValid(parsedDate)
                ? this.$moment(date).format('DD.MM.YYYY')
                : 'Não informado';
        },
        documentDate(document) {
            return document.created_at
                ? `Publicado em ${this.$moment(document.created_at).format('DD.MM.YYYY')}`
                : 'Documento do processo';
        },
        formatCount(count) {
            return String(count).padStart(2, '0');
        },
        formatIndex(index) {
            return String(index).padStart(2, '0');
        },
        documentCountLabel(count) {
            return `${count} ${count === 1 ? 'documento' : 'documentos'}`;
        },
        initials(name = '') {
            return name
                .split(' ')
                .filter(Boolean)
                .slice(0, 2)
                .map((word) => word[0])
                .join('')
                .toUpperCase();
        },
        closeDialog() {
            this.dialog = false;
        },
        navigateToCreateUserEvents(id) {
            this.$router.push({ path: '/habilitacoes-divergencias/novo', query: { id } });
        }
    }
};

function currentDateIsValid(date) {
    return date instanceof Date && !Number.isNaN(date.getTime());
}
</script>

<style scoped lang="scss">
.case-detail {
    padding-bottom: 0;
    background: var(--pbb-cream);
}
.case-hero {
    position: relative;
    overflow: hidden;
    padding: 28px 0 72px;
    border-bottom: 1px solid rgba(201, 132, 80, 0.45);
    background: var(--pbb-navy-deep);
    color: #fff;
}
.case-hero__breadcrumb {
    position: relative;
    z-index: 1;
    display: flex;
    gap: 12px;
    margin-bottom: 54px;
    color: rgba(255, 255, 255, 0.45);
    font-size: 11px;
}
.case-hero__breadcrumb a {
    color: rgba(255, 255, 255, 0.68);
    text-decoration: none;
}
.case-hero__breadcrumb a:hover {
    color: #e5a476;
}
.case-hero__layout {
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-columns: minmax(0, 1fr) 280px;
    gap: clamp(60px, 10vw, 150px);
    align-items: end;
}
.case-hero__nature {
    display: inline-flex;
    gap: 12px;
    align-items: center;
    color: #e5a476;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
}
.case-hero__nature i {
    width: 28px;
    height: 2px;
    background: currentColor;
}
.case-hero h1 {
    max-width: 850px;
    margin: 22px 0 36px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: clamp(42px, 5.5vw, 74px);
    font-weight: 400;
    line-height: 1.02;
    letter-spacing: -0.055em;
    overflow-wrap: anywhere;
}
.case-hero__process {
    display: flex;
    gap: 18px;
    align-items: baseline;
}
.case-hero__process span {
    color: rgba(255, 255, 255, 0.46);
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
}
.case-hero__process strong {
    color: rgba(255, 255, 255, 0.86);
    font-size: 15px;
    font-weight: 500;
    letter-spacing: 0.025em;
}
.case-hero__aside {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}
.case-hero__logo {
    display: grid;
    width: 78px;
    height: 78px;
    margin-bottom: 22px;
    place-items: center;
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.18);
    border-radius: 9px;
    background: rgba(255, 255, 255, 0.08);
    color: #e5a476;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 22px;
    font-weight: 600;
}
.case-hero__logo img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    background: #fff;
}
.case-hero__action {
    display: inline-flex;
    gap: 12px;
    align-items: center;
    margin-top: 26px;
    padding: 14px 18px;
    border: 1px solid rgba(255, 255, 255, 0.35);
    border-radius: 7px;
    background: transparent;
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
}
.case-hero__action + .case-hero__action {
    margin-top: 12px;
}
a.case-hero__action {
    text-decoration: none;
}
.case-hero__action .v-icon {
    color: #e5a476;
}
.case-hero__request {
    display: grid;
    gap: 4px;
    margin-top: 26px;
    padding: 14px 18px;
    border: 1px solid rgba(229, 164, 118, 0.45);
    border-radius: 7px;
    background: rgba(255, 255, 255, 0.06);
    font-size: 12px;
}
.case-hero__request span {
    color: rgba(255, 255, 255, 0.6);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-size: 10px;
}
.case-hero__request strong {
    font-size: 15px;
}
.case-hero__request small {
    color: rgba(255, 255, 255, 0.6);
}
.case-hero__request a {
    margin-top: 6px;
    color: #e5a476;
    font-weight: 700;
}
.case-content {
    padding: 0 0 110px;
}
.case-facts {
    position: relative;
    z-index: 2;
    display: grid;
    grid-template-columns: 1.35fr 1fr 1fr 0.65fr;
    margin-top: -24px;
    overflow: hidden;
    border: 1px solid #dedbd4;
    border-radius: 9px;
    background: #fff;
    box-shadow: 0 15px 35px rgba(0, 28, 49, 0.07);
}
.case-facts--five {
    grid-template-columns: 1.35fr 1fr 1fr 1fr 0.65fr;
}
.case-fact {
    min-width: 0;
    padding: 24px 28px;
    border-left: 1px solid #e5e1da;
}
.case-fact:first-child {
    border-left: 0;
}
.case-fact span {
    display: block;
    margin-bottom: 9px;
    color: #8a9399;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
}
.case-fact strong {
    display: block;
    color: var(--pbb-navy);
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.4;
    overflow-wrap: anywhere;
}
.case-overview {
    display: grid;
    grid-template-columns: minmax(0, 1.25fr) minmax(300px, 0.75fr);
    gap: clamp(70px, 10vw, 140px);
    padding: 96px 0;
}
.case-section-label {
    color: var(--pbb-copper);
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.13em;
    text-transform: uppercase;
}
.case-overview h2,
.case-documents__header h2 {
    margin: 16px 0 28px;
    color: var(--pbb-navy);
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: clamp(34px, 4vw, 50px);
    font-weight: 400;
    line-height: 1.08;
    letter-spacing: -0.045em;
}
.case-overview__description {
    max-width: 700px;
    color: #53616a;
    font-size: 15px;
    line-height: 1.9;
}
.case-overview__description::v-deep p:last-child {
    margin-bottom: 0;
}
.case-overview__fallback {
    color: var(--pbb-muted);
    line-height: 1.8;
}
.case-overview__aside {
    padding-top: 4px;
}
.case-overview__aside dl {
    margin: 18px 0 0;
}
.case-overview__aside dl > div {
    padding: 20px 0;
    border-top: 1px solid #dcd8d0;
}
.case-overview__aside dt {
    margin-bottom: 7px;
    color: #8a9399;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.09em;
    text-transform: uppercase;
}
.case-overview__aside dd {
    margin: 0;
    color: var(--pbb-navy);
    font-size: 13px;
    font-weight: 600;
    line-height: 1.55;
}
.case-timeline {
    padding-top: 90px;
}
.case-timeline__list {
    position: relative;
    margin: 36px 0 0;
    padding: 0;
    list-style: none;
}
.case-timeline__list::before {
    position: absolute;
    top: 8px;
    bottom: 8px;
    left: 108px;
    width: 1px;
    background: #dedbd4;
    content: '';
}
.case-timeline__item {
    position: relative;
    display: grid;
    grid-template-columns: 96px 1fr;
    gap: 36px;
    padding: 0 0 26px;
}
.case-timeline__item::before {
    position: absolute;
    top: 6px;
    left: 104px;
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: var(--pbb-copper, #986137);
    content: '';
}
.case-timeline__item time {
    color: var(--pbb-navy);
    font-size: 13px;
    font-weight: 700;
}
.case-timeline__item strong {
    display: block;
    color: var(--pbb-navy);
    font-size: 15px;
}
.case-timeline__item p {
    margin: 6px 0 0;
    color: var(--pbb-muted);
    font-size: 14px;
    line-height: 1.7;
    white-space: pre-line;
}
@media (max-width: 600px) {
    .case-timeline__list::before {
        left: 4px;
    }
    .case-timeline__item {
        grid-template-columns: 1fr;
        gap: 4px;
        padding-left: 24px;
    }
    .case-timeline__item::before {
        left: 0;
    }
}
.case-documents {
    padding-top: 80px;
    border-top: 1px solid #d8d4cc;
}
.case-documents__header {
    display: grid;
    grid-template-columns: minmax(0, 1.15fr) minmax(280px, 0.85fr);
    gap: 80px;
    align-items: end;
    margin-bottom: 44px;
}
.case-documents__header h2 {
    margin-bottom: 0;
}
.case-documents__header p {
    max-width: 430px;
    margin: 0 0 4px;
    color: var(--pbb-muted);
    font-size: 13px;
    line-height: 1.75;
}
.case-documents__workspace {
    display: grid;
    grid-template-columns: 285px minmax(0, 1fr);
    overflow: hidden;
    border: 1px solid #dcd8d0;
    border-radius: 9px;
    background: #fff;
}
.case-documents__tabs {
    padding: 20px;
    border-right: 1px solid #e1ddd6;
    background: #f0ece6;
}
.case-documents__tabs button {
    display: grid;
    grid-template-columns: 28px minmax(0, 1fr) auto;
    gap: 10px;
    align-items: center;
    width: 100%;
    min-height: 70px;
    padding: 14px 13px;
    border: 0;
    border-radius: 7px;
    background: transparent;
    color: #647079;
    text-align: left;
    cursor: pointer;
    transition: color 180ms ease, background-color 180ms ease;
}
.case-documents__tabs button + button {
    margin-top: 5px;
}
.case-documents__tabs button > span {
    color: var(--pbb-copper);
    font-size: 9px;
    font-weight: 700;
}
.case-documents__tabs button strong {
    font-size: 12px;
    font-weight: 600;
    line-height: 1.4;
}
.case-documents__tabs button small {
    display: grid;
    width: 25px;
    height: 25px;
    place-items: center;
    border-radius: 50%;
    background: rgba(1, 42, 74, 0.07);
    font-size: 9px;
}
.case-documents__tabs button:hover,
.case-documents__tabs button.is-active {
    background: var(--pbb-navy);
    color: #fff;
}
.case-documents__tabs button.is-active small {
    background: rgba(255, 255, 255, 0.12);
}
.case-documents__panel {
    min-height: 360px;
    padding: 36px clamp(28px, 4vw, 52px);
}
.case-documents__panel-head {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 24px;
    padding-bottom: 24px;
    border-bottom: 1px solid #e1ddd6;
}
.case-documents__panel-head span {
    color: #92999e;
    font-size: 8px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
}
.case-documents__panel-head h3 {
    margin: 7px 0 0;
    color: var(--pbb-navy);
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 20px;
    font-weight: 500;
    letter-spacing: -0.025em;
}
.case-documents__panel-head > strong {
    color: var(--pbb-muted);
    font-size: 10px;
    font-weight: 600;
}
.document-row {
    display: grid;
    grid-template-columns: 26px 42px minmax(0, 1fr) auto;
    gap: 14px;
    align-items: center;
    min-height: 82px;
    border-bottom: 1px solid #e5e1da;
    color: var(--pbb-navy);
    text-decoration: none;
}
.document-row__number {
    color: var(--pbb-copper);
    font-size: 9px;
    font-weight: 700;
}
.document-row__icon,
.case-documents__empty-icon {
    display: grid;
    width: 40px;
    height: 40px;
    place-items: center;
    border-radius: 7px;
    background: #f0ece6;
}
.document-row__icon .v-icon,
.case-documents__empty-icon .v-icon {
    color: var(--pbb-copper);
}
.document-row__content {
    display: flex;
    flex-direction: column;
    gap: 5px;
}
.document-row__content strong {
    font-size: 13px;
    font-weight: 600;
}
.document-row__content small {
    color: #8c959b;
    font-size: 9px;
}
.document-row__action {
    display: inline-flex;
    gap: 8px;
    align-items: center;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}
.document-row__action .v-icon {
    color: var(--pbb-copper);
    transition: transform 180ms ease;
}
.document-row:hover .document-row__action .v-icon {
    transform: translate(3px, -3px);
}
.case-documents__empty {
    display: flex;
    gap: 18px;
    align-items: center;
    min-height: 205px;
    color: var(--pbb-navy);
}
.case-documents__empty strong {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 15px;
    font-weight: 500;
}
.case-documents__empty p {
    margin: 7px 0 0;
    color: var(--pbb-muted);
    font-size: 12px;
}
@media (max-width: 900px) {
    .case-hero__layout {
        grid-template-columns: minmax(0, 1fr) 210px;
        gap: 50px;
    }
    .case-facts {
        grid-template-columns: repeat(2, 1fr);
    }
    .case-fact:nth-child(3) {
        border-left: 0;
        border-top: 1px solid #e5e1da;
    }
    .case-fact:nth-child(4) {
        border-top: 1px solid #e5e1da;
    }
    .case-facts--five .case-fact:nth-child(n + 3) {
        border-top: 1px solid #e5e1da;
    }
    .case-facts--five .case-fact:nth-child(odd) {
        border-left: 0;
    }
    .case-overview {
        grid-template-columns: 1fr;
        gap: 60px;
    }
    .case-documents__header {
        grid-template-columns: 1fr;
        gap: 20px;
    }
    .case-documents__workspace {
        grid-template-columns: 1fr;
    }
    .case-documents__tabs {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 5px;
        border-right: 0;
        border-bottom: 1px solid #e1ddd6;
    }
    .case-documents__tabs button + button {
        margin-top: 0;
    }
}
@media (max-width: 600px) {
    .case-hero {
        padding-bottom: 54px;
    }
    .case-hero__breadcrumb {
        margin-bottom: 38px;
    }
    .case-hero__layout {
        grid-template-columns: 1fr;
        gap: 40px;
    }
    .case-hero h1 {
        margin-bottom: 28px;
        font-size: 42px;
    }
    .case-hero__process {
        flex-direction: column;
        gap: 7px;
    }
    .case-hero__aside {
        display: grid;
        grid-template-columns: 64px minmax(0, 1fr);
        gap: 18px;
        align-items: center;
    }
    .case-hero__logo {
        width: 64px;
        height: 64px;
        margin: 0;
    }
    .case-hero__action {
        grid-column: 1 / -1;
        margin-top: 4px;
    }
    .case-facts {
        grid-template-columns: 1fr;
        margin-top: 20px;
        box-shadow: none;
    }
    .case-fact,
    .case-fact:nth-child(2) {
        border-top: 1px solid #e5e1da;
        border-left: 0;
    }
    .case-fact:first-child {
        border-top: 0;
    }
    .case-overview {
        padding: 70px 0;
    }
    .case-documents {
        padding-top: 64px;
    }
    .case-documents__tabs {
        grid-template-columns: 1fr;
    }
    .case-documents__panel {
        min-height: 320px;
        padding: 28px 22px;
    }
    .case-documents__panel-head {
        align-items: flex-start;
        flex-direction: column;
        gap: 8px;
    }
    .document-row {
        grid-template-columns: 24px 38px minmax(0, 1fr);
    }
    .document-row__action {
        grid-column: 3;
        padding-bottom: 14px;
    }
}
@media (prefers-reduced-motion: reduce) {
    .case-documents__tabs button,
    .document-row__action .v-icon {
        transition: none;
    }
}
</style>
